export type AccountType = "debit" | "savings" | "cash" | "investment" | "credit_card" | "loan" | "property";
export type AccountFamily = "debit" | "credit" | "investment" | "property";
export type AccountSubtype = "checking" | "savings" | "cash" | "current" | "card" | "department_store" | "mortgage" | "auto" | "personal" | "capital" | "flow"
  | "house" | "land" | "vehicle" | "other_asset";

export const FAMILY_LABELS: Record<AccountFamily, string> = { debit: "Débito", credit: "Crédito", investment: "Inversión", property: "Bienes" };
export const FAMILY_SUBTYPES: Record<AccountFamily, AccountSubtype[]> = {
  debit: ["checking", "savings", "cash", "current"],
  credit: ["card", "department_store", "mortgage", "auto", "personal"],
  investment: ["capital", "flow"],
  property: ["house", "land", "vehicle", "other_asset"],
};
export const SUBTYPE_LABELS: Record<AccountSubtype, string> = {
  checking: "Chequera", savings: "Ahorros", cash: "Efectivo", current: "Gasto corriente",
  card: "Tarjeta de crédito", department_store: "Departamental", mortgage: "Hipotecario", auto: "Automotriz", personal: "Personal",
  capital: "Capital", flow: "Flujo",
  house: "Casa o departamento", land: "Terreno", vehicle: "Auto", other_asset: "Otro",
};
export const familyOf = (t: AccountType): AccountFamily => (t === "credit_card" || t === "loan" ? "credit" : t === "investment" ? "investment" : t === "property" ? "property" : "debit");
export const accountKindLabel = (a: { account_type: AccountType; account_subtype?: AccountSubtype | null }) =>
  a.account_subtype ? SUBTYPE_LABELS[a.account_subtype] : ACCOUNT_TYPE_LABELS[a.account_type];
export type TxType = "income" | "expense" | "transfer" | "adjustment";
export type CategoryGroup = "essentials" | "comfort" | "debt" | "savings_investment" | "income";

export interface AccountMeta {
  credit_limit?: number | null;
  statement_day?: number | null;
  payment_day?: number | null;
  utilization_ratio?: number | null;
  available_credit?: number | null;
  over_limit_by?: number;
  loan_monthly_payment?: number | null;
  interest_paid_year?: number;
  linked_loan?: { id: string; name: string; owed: number; in_mxn?: boolean } | null;
  linked_asset?: { id: string; name: string } | null;
  settled_on?: string | null;
  equity?: number;
  msi_commitments?: { total: number; by_month: { month: string; amount: number }[] };
}

export interface Account {
  id: string;
  name: string;
  account_type: AccountType;
  account_subtype?: AccountSubtype | null;
  institution: string | null;
  opening_balance: number;
  credit_limit: number | null;
  statement_day: number | null;
  payment_day: number | null;
  loan_monthly_payment: number | null;
  notes?: string | null;
  purchase_value?: number | null;
  purchase_date?: string | null;
  linked_loan_id?: string | null;
  depreciation_on?: boolean;
  depreciation_rate?: number | null;
  disposed_on?: string | null;
  disposal_reason?: string | null;
  current_balance: number;
  currency: Currency;
  balance_mxn: number;
  fx_rate: number | null;
  is_liability: boolean;
  is_archived: boolean;
  has_transactions: boolean;
  meta: AccountMeta;
}

export interface AccountsResponse {
  assets_total: number;
  liabilities_total: number;
  net_worth: number;
  liquid_total: number;
  goods_total?: number;
  invested_total: number;
  growth: number;
  history: { month: string; net_worth: number }[];
  accounts: Account[];
}

export interface Category {
  id: string;
  name: string;
  type: "income" | "expense" | "transfer";
  group: CategoryGroup;
  is_archived?: boolean;
  description?: string | null;
}

export interface Transaction {
  id: string;
  date: string;
  type: TxType;
  description: string;
  amount: number;
  notes: string | null;
  category_id: string | null;
  budget_item_id?: string | null;
  group?: "essentials" | "comfort" | "debt" | null;
  category_name: string | null;
  source_account_id: string | null;
  source_account_name: string | null;
  destination_account_id: string | null;
  destination_account_name: string | null;
  flow: "in" | "out" | "neutral";
  is_investment_return: boolean;
  is_loan_payment: boolean;
  is_savings_withdrawal?: boolean;
  transfer_kind?: TransferKind | null;
  msi_total?: number | null;
  msi_schedule?: { date: string; amount: number }[];
  source_native?: number | null;
  dest_native?: number | null;
  fx_rate?: number | null;
  original_amount?: number | null;
  original_currency?: Currency | null;
  source_currency?: Currency;
  destination_currency?: Currency;
}

export type Currency = "MXN" | "USD" | "EUR";
export const CURRENCIES: { value: Currency; label: string }[] = [
  { value: "MXN", label: "Pesos (MXN)" }, { value: "USD", label: "Dólares (USD)" }, { value: "EUR", label: "Euros (EUR)" },
];

export type TransferKind = "between" | "savings" | "credit" | "withdrawal" | "asset_buy" | "asset_sell";
export const TRANSFER_KIND_LABELS: Record<TransferKind, string> = { between: "Entre cuentas", savings: "Ahorro/Inversión", credit: "Pago de crédito", withdrawal: "Retiro de ahorro", asset_buy: "Compra de bien", asset_sell: "Venta de bien" };
export const TRANSFER_KIND_ORDER: { kind: TransferKind; label: string }[] = [
  { kind: "savings", label: "Ahorro/Inversión" }, { kind: "credit", label: "Pago de crédito" },
  { kind: "between", label: "Mov. entre cuentas" }, { kind: "withdrawal", label: "Retiro de ahorro" },
  { kind: "asset_buy", label: "Compra de bien" }, { kind: "asset_sell", label: "Venta de bien" },
];
const SAVINGS_TYPES: AccountType[] = ["savings", "investment"];
const RULES: Record<TransferKind, [AccountType[], AccountType[]]> = {
  savings: [["debit"], SAVINGS_TYPES], credit: [["debit"], ["credit_card", "loan"]],
  between: [["debit", "cash"], ["debit", "cash"]], withdrawal: [SAVINGS_TYPES, ["debit"]],
  asset_buy: [["debit"], ["property"]], asset_sell: [["property"], ["debit"]],
};
export const srcAllows = (k: TransferKind, t: AccountType) => RULES[k][0].includes(t);
export const kindAllows = (k: TransferKind, t: AccountType) => RULES[k][1].includes(t);
const TYPE_SUBTYPES: Record<AccountType, AccountSubtype[]> = {
  debit: ["checking", "current"], savings: ["savings"], cash: ["cash"], investment: ["capital", "flow"],
  credit_card: ["card", "department_store"], loan: ["mortgage", "auto", "personal"], property: ["house", "land", "vehicle", "other_asset"],
};
export const subsFor = (types: AccountType[]) => types.flatMap((t) => TYPE_SUBTYPES[t]);
export const transferSubs = (k: TransferKind, side: 0 | 1) => subsFor(RULES[k][side]);
export const familyOfSub = (s: AccountSubtype) => (Object.keys(FAMILY_SUBTYPES) as AccountFamily[]).find((f) => FAMILY_SUBTYPES[f].includes(s)) ?? "debit";

export interface MonthSummary {
  income_total: number;
  expense_total: number;
  balance: number;
  loan_payments_total: number;
  transfer_total: number;
  adjustment_total: number;
  count: number;
}

export interface TxListResponse {
  month: string;
  summary: MonthSummary;
  items: Transaction[];
}

export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  debit: "Débito",
  savings: "Ahorro",
  cash: "Efectivo",
  investment: "Inversión",
  credit_card: "Tarjeta de crédito",
  loan: "Préstamo",
  property: "Bien",
};

export const GROUP_LABELS: Record<CategoryGroup, string> = {
  essentials: "Esenciales (Necesidades)",
  comfort: "Comodidad (Estilo de Vida)",
  debt: "Deuda",
  savings_investment: "Ahorro e Inversión",
  income: "Ingreso",
};

export const TX_TYPE_LABELS: Record<TxType, string> = {
  expense: "Gasto",
  income: "Ingreso",
  transfer: "Traspaso",
  adjustment: "Ajuste",
};

export const isLiabilityType = (t: AccountType) => t === "credit_card" || t === "loan";

export const accountLabel = (tx: Transaction) =>
  tx.type === "transfer"
    ? `${tx.source_account_name ?? "—"} → ${tx.destination_account_name ?? "—"}`
    : tx.source_account_name ?? tx.destination_account_name ?? "—";
